import { createMarketingEditorial } from "../editorial";
import { localize } from "../locale";
import { mountCinema } from "../cinema";
import { mountMobileRoute } from "../cinema/mobile";
import { createMarketingBlocks } from "../blocks";
import { DomMarketingNavigationMount, MarketingNavigationController } from "../navigation";
import { createOfferController, type OfferController, type OfferTargetConfig } from "../offers";
import {
  DomLiveSiteFlipPort,
  UsesProofController,
  type SharedWorldIdentitySet,
  type UsesWorldTransitionState,
  type UsesWorldPort,
} from "../proofs/uses";
import {
  SocialShareProofController,
  type SocialShareWorldPort,
  type SocialShareWorldPreviewInput,
} from "../proofs/share";
import { WhereProofController } from "../proofs/where";
import { LinearMarketingTimeline } from "../timeline";
import {
  WorldContinuityController,
  type WorldObjectIdentities,
} from "../world";
import type { ExperienceSnapshot, MarketingLayout } from "../types";
import {
  MarketingExperience,
  MarketingPresentation,
  type MarketingExperienceOptions,
  type MarketingFramePort,
  type MarketingPresentationPort,
} from ".";
import {
  LinearMarketingHostPublisher,
  LinearMarketingPhysicalHost,
  bindLegacyWorldMount,
  type LegacyMarketingWorldHost,
  type LegacyMarketingWorldMount,
  type MarketingHostPublisher,
  type MarketingPhysicalHost,
} from "./host";
import { VERIFIED_PUBLIC_ROUTES } from "./verifiedPublicRoutes";
import { OFFER_PLAN_MONTHS } from "../offers/pricing";
import { DOWNLOAD_LINKS } from "../../download-links";

export * from "./host";
export { VERIFIED_PUBLIC_ROUTES } from "./verifiedPublicRoutes";

export interface MarketingOffersLifecycle {
  setInstallTargets(config: OfferTargetConfig): void;
  load(locale: string, signal: AbortSignal): Promise<unknown>;
  snapshot(): ReturnType<OfferController["snapshot"]>;
  destroy(): void;
}

export interface MarketingComposition {
  readonly experience: MarketingExperience;
  readonly offers: MarketingOffersLifecycle;
}

export interface MarketingCompositionDependencies {
  createPresentation(root: HTMLElement): MarketingPresentationPort;
  createTimeline(): LinearMarketingTimeline;
  createWorld(worldMount: LegacyMarketingWorldMount, layout: MarketingLayout): MarketingFramePort;
  createNavigation(header: HTMLElement): MarketingFramePort;
  createEditorial(root: HTMLElement): MarketingFramePort;
  createWhere(root: HTMLElement): MarketingFramePort;
  createUses(
    root: HTMLElement,
    worldMount: LegacyMarketingWorldMount,
    liveSiteNode: HTMLElement,
  ): MarketingFramePort;
  createShare(root: HTMLElement, worldMount: LegacyMarketingWorldMount): MarketingFramePort;
  createBlocks(root: HTMLElement): MarketingFramePort;
  createOffers(root: HTMLElement): MarketingOffersLifecycle;
  createExperience(root: HTMLElement, options: MarketingExperienceOptions): MarketingExperience;
}

export interface MarketingCompositionRuntime {
  readonly experience: MarketingExperience;
  readonly publisher: MarketingHostPublisher;
  readonly physicalHost: MarketingPhysicalHost;
  readonly destroyed: boolean;
  renderCurrent(): ExperienceSnapshot | null;
  destroy(): void;
}

export interface MarketingMountOptions {
  readonly mountCinema?: (view:Window,root:HTMLElement)=>{destroy():void};
  readonly window?: Window;
  readonly document?: Document;
  readonly dependencies?: MarketingCompositionDependencies;
  readonly now?: () => number;
}

declare global {
  interface Window {
    __BB_MARKETING_EXPERIENCE__?: MarketingExperience;
    __BB_MARKETING_HOST__?: MarketingHostPublisher;
    __BB_MARKETING_PHYSICAL_HOST__?: MarketingPhysicalHost;
    __BB_MARKETING_RUNTIME__?: MarketingCompositionRuntime;
    __BB_MARKETING_WORLD_MOUNT__?: LegacyMarketingWorldMount;
    __BB_MARKETING_WORLD_HOST__?: LegacyMarketingWorldHost;
    __BB_RECOVER_MARKETING_BOOTSTRAP__?: () => boolean;
  }
}

const VERIFIED_REGISTRATION_SELECTOR =
  "a[data-verified-registration][data-registration-source][href]";

export function readVerifiedOfferTargets(root: ParentNode): OfferTargetConfig {
  const anchor = root.querySelector<HTMLAnchorElement>(VERIFIED_REGISTRATION_SELECTOR);
  if (!anchor) throw new Error("Marketing route is missing its verified registration anchor.");
  const verified = VERIFIED_PUBLIC_ROUTES.registration;
  const source = anchor.getAttribute("data-registration-source");
  if (source !== verified.evidence) {
    throw new Error("Marketing registration source does not match the independently verified route.");
  }
  const rawHref = anchor.getAttribute("href");
  const base = anchor.ownerDocument?.baseURI ?? "https://blenderbridge.com/";
  const registration = rawHref ? new URL(rawHref, base) : null;
  const allowedOrigin = registration && Object.values(verified.origins).some(origin => origin === registration.origin);
  if (!registration || !allowedOrigin || registration.pathname !== verified.pathname
    || registration.username || registration.password || registration.hash
    || [...registration.searchParams.keys()].some(key => !["lang","v"].includes(key))
    || registration.searchParams.getAll("lang").length > 1
    || registration.searchParams.getAll("v").length > 1
    || (registration.searchParams.has("v") && !/^(0|[0-9a-f]{32})$/.test(registration.searchParams.get("v")!))) {
    throw new Error("Registration anchor does not match the verified public origin and route.");
  }
  /*
   * Оформление ведёт на тот же кабинет, что и регистрация, и адрес берётся из
   * уже проверенного происхождения — не из отдельной настройки, которую можно
   * перепутать и увести покупателя на чужой хост.
   *
   * Срок передаётся кодом тарифа, а не ценой: цену страница оформления
   * спрашивает у сервера, и переписать её в ссылке невозможно.
   */
  const checkout: Record<number, string> = {};
  if (import.meta.env.VITE_BB_CHECKOUT_ENABLED === "true") {
    for (const months of OFFER_PLAN_MONTHS) {
      const target = new URL(VERIFIED_PUBLIC_ROUTES.checkout.pathname, registration.origin);
      target.searchParams.set("plan", `BB_${months}M`);
      const language = registration.searchParams.get("lang");
      if (language) target.searchParams.set("lang", language);
      checkout[months] = target.href;
    }
  }

  return Object.freeze({
    registration: registration.href,
    windowsInstaller: DOWNLOAD_LINKS.windows,
    chromeWebStore: DOWNLOAD_LINKS.extension,
    checkout,
  });
}

export function createLegacyWorldPort(
  worldMount: LegacyMarketingWorldMount,
  initialLayout: MarketingLayout,
): MarketingFramePort {
  const worldController = new WorldContinuityController({
    geometry: worldMount.measureGeometry(initialLayout),
    // The controller treats identities opaquely; exact references prove ownership
    // of the existing Three scene objects in every public WorldSnapshot.
    objects: worldMount.objects as unknown as WorldObjectIdentities,
  });
  return {
    render: (frame, now) => {
      const snapshot = worldController.render(frame, now);
      if (frame.handoff > 0) worldMount.applyWorldSnapshot(snapshot);
      return snapshot;
    },
    resize: (viewport, layout) => {
      const geometry = worldMount.measureGeometry(layout);
      worldController.resize(viewport, geometry);
      worldMount.resize?.(viewport, geometry);
    },
    destroy: () => {
      worldController.destroy();
      worldMount.destroy?.();
    },
  };
}

const assertUsesIdentities = (
  expected: SharedWorldIdentitySet<object>,
  actual: SharedWorldIdentitySet<object>,
): void => {
  if (
    actual !== expected
    || actual.cube !== expected.cube
    || actual.cyanLamp !== expected.cyanLamp
    || actual.orangeLamp !== expected.orangeLamp
  ) throw new Error("Uses proof attempted to replace shared world identities.");
};

export function createUsesWorldPort(
  worldMount: LegacyMarketingWorldMount,
): UsesWorldPort<object> {
  const identities = Object.freeze({
    cube: worldMount.objects.cube,
    cyanLamp: worldMount.objects.cyanLamp,
    orangeLamp: worldMount.objects.orangeLamp,
  });
  return Object.freeze({
    identities,
    apply: (state: UsesWorldTransitionState, actual: SharedWorldIdentitySet<object>) => {
      assertUsesIdentities(identities, actual);
      worldMount.applyUsesWorldState(state);
    },
    clear: (actual: SharedWorldIdentitySet<object>) => {
      assertUsesIdentities(identities, actual);
      worldMount.clearUsesWorldState();
    },
  });
}

export function createSocialShareWorldPort(
  worldMount: LegacyMarketingWorldMount,
): SocialShareWorldPort {
  const objects = Object.freeze({
    cube: worldMount.objects.cube,
    grid: worldMount.objects.grid,
    camera: worldMount.objects.camera,
    cyanLamp: worldMount.objects.cyanLamp,
    orangeLamp: worldMount.objects.orangeLamp,
  }) as unknown as WorldObjectIdentities;
  return Object.freeze({
    objects,
    applySharePreview: (input: SocialShareWorldPreviewInput) => {
      if (input.objects !== objects) {
        throw new Error("Social Share proof attempted to replace shared world identities.");
      }
      worldMount.applySharePreview(input);
    },
    clearSharePreview: () => worldMount.clearSharePreview(),
  });
}

const productionDependencies: MarketingCompositionDependencies = {
  createPresentation: (root) => new MarketingPresentation(root),
  createTimeline: () => new LinearMarketingTimeline(),
  createWorld: createLegacyWorldPort,
  createNavigation: (header) => {
    /* Шапка ведёт себя по прокрутке ДОКУМЕНТА, а не по ходу маршрута: в первой
       половине сайта ход маршрута ещё нулевой, и направления там взять неоткуда.
       Раньше из-за этого шапка не скрывалась до самого маркетинга. */
    const controller = new MarketingNavigationController({
      mount: new DomMarketingNavigationMount(header),
    });
    const view = header.ownerDocument.defaultView;
    if (view) {
      const report = () => controller.observeDocumentScroll(view.scrollY);
      report();
      view.addEventListener("scroll", report, { passive: true });
    }
    return controller;
  },
  createEditorial: createMarketingEditorial,
  createWhere: (root) => new WhereProofController(root),
  createUses: (root, worldMount, liveSiteNode) => new UsesProofController(root, {
    world: createUsesWorldPort(worldMount),
    siteFlip: new DomLiveSiteFlipPort(liveSiteNode),
    highlights: true,
  }),
  createShare: (root, worldMount) => new SocialShareProofController(root, {
    world: createSocialShareWorldPort(worldMount),
    nativeShareSupported: false,
  }),
  createBlocks: createMarketingBlocks,
  createOffers: createOfferController,
  createExperience: (root, options) => new MarketingExperience(root, options),
};

export function createMarketingComposition(
  root: HTMLElement,
  header: HTMLElement,
  worldMount: LegacyMarketingWorldMount,
  offerTargets: OfferTargetConfig,
  dependencies: MarketingCompositionDependencies = productionDependencies,
): MarketingComposition {
  const cleanup: Array<() => void> = [];
  let worldCreated = false;
  try {
    const presentation = dependencies.createPresentation(root);
    cleanup.push(() => presentation.destroy());
    const initialLayout = presentation.measure();
    const timeline = dependencies.createTimeline();
    timeline.measure(initialLayout);
    const world = dependencies.createWorld(worldMount, initialLayout);
    worldCreated = true;
    cleanup.push(() => world.destroy?.());
    const navigation = dependencies.createNavigation(header);
    cleanup.push(() => navigation.destroy?.());
    const editorial = dependencies.createEditorial(root);
    cleanup.push(() => editorial.destroy?.());
    const where = dependencies.createWhere(root);
    cleanup.push(() => where.destroy?.());
    const liveSiteNode = root.querySelector<HTMLElement>("[data-site-live-fragment]");
    if (!liveSiteNode) throw new Error("Marketing route is missing its exact live-site FLIP node.");
    const uses = dependencies.createUses(root, worldMount, liveSiteNode);
    cleanup.push(() => uses.destroy?.());
    const share = dependencies.createShare(root, worldMount);
    cleanup.push(() => share.destroy?.());
    const blocks = dependencies.createBlocks(root);
    cleanup.push(() => blocks.destroy?.());
    const offers = dependencies.createOffers(root);
    cleanup.push(() => offers.destroy());
    offers.setInstallTargets(offerTargets);
    const experience = dependencies.createExperience(root, {
      timeline,
      presentation,
      world,
      navigation,
      editorial,
      where,
      uses,
      share,
      blocks,
      offers,
    });
    return { experience, offers };
  } catch (error) {
    for (const destroy of cleanup.reverse()) {
      try { destroy(); } catch { /* Preserve the originating composition failure. */ }
    }
    if (!worldCreated) {
      try { worldMount.destroy?.(); } catch { /* Preserve the originating composition failure. */ }
    }
    throw error;
  }
}

export function mountMarketingExperience(
  options: MarketingMountOptions = {},
): MarketingCompositionRuntime | null {
  const targetWindow = options.window ?? (typeof window === "undefined" ? undefined : window);
  const targetDocument = options.document ?? (typeof document === "undefined" ? undefined : document);
  if (!targetWindow || !targetDocument) return null;
  if (targetDocument.documentElement.dataset.marketingBootstrapFallback === "true") return null;
  const existing = targetWindow.__BB_MARKETING_RUNTIME__;
  if (existing && !existing.destroyed) return existing;

  const marketingRoot = targetDocument.querySelector<HTMLElement>("[data-marketing-route]");
  const header = targetDocument.querySelector<HTMLElement>("#nav");
  const worldHost = targetWindow.__BB_MARKETING_WORLD_HOST__;
  if (!marketingRoot || !header || !worldHost) {
    targetWindow.__BB_RECOVER_MARKETING_BOOTSTRAP__?.();
    targetDocument.body?.classList.remove("marketing-runtime-pending");
    if (marketingRoot) {
      marketingRoot.dataset.marketingStaticFallback = "true";
      targetDocument.documentElement.dataset.webglFallback = "true";
    }
    return null;
  }
  let worldMount: LegacyMarketingWorldMount;
  let composition: MarketingComposition;
  try {
    const offerTargets = readVerifiedOfferTargets(marketingRoot);
    worldMount = worldHost.bind(bindLegacyWorldMount);
    composition = createMarketingComposition(
      marketingRoot,
      header,
      worldMount,
      offerTargets,
      options.dependencies,
    );
  } catch (error) {
    console.error("[marketing composition]", error);
    targetWindow.__BB_RECOVER_MARKETING_BOOTSTRAP__?.();
    targetDocument.documentElement.dataset.webglFallback = "true";
    targetDocument.documentElement.dataset.marketingBootstrapFallback = "true";
    targetDocument.body?.classList.remove("marketing-runtime-pending");
    marketingRoot.dataset.marketingStaticFallback = "true";
    delete targetWindow.__BB_MARKETING_WORLD_MOUNT__;
    return null;
  }
  targetWindow.__BB_MARKETING_WORLD_MOUNT__ = worldMount;
  const publisher = new LinearMarketingHostPublisher(composition.experience);
  const physicalHost = new LinearMarketingPhysicalHost(publisher);
  const lifecycleAbort = new AbortController();
  const now = options.now ?? (() => performance.now());
  let destroyed = false;
  let cinema: {destroy():void}|undefined;

  const renderCurrent = (): ExperienceSnapshot | null => {
    if (destroyed) return null;
    const frameNow = now();
    const input = worldMount.readHostInput(frameNow);
    return publisher.tick(input.rawPhysicalTravel, frameNow, input.viewport, input.motion);
  };
  const renderRestoredScroll = (): void => {
    if (destroyed) return;
    composition.experience.resize();
    renderCurrent();
  };
  const loadOffers = (locale: string): void => {
    if (!destroyed) void composition.offers.load(locale || "en", lifecycleAbort.signal);
  };
  const onLocaleChange = (event: Event): void => {
    const locale = (event as CustomEvent<{ locale?: string }>).detail?.locale;
    loadOffers(locale ?? targetDocument.documentElement.lang);
    localize(marketingRoot);renderRestoredScroll();
  };
  const onResize = (): void => renderRestoredScroll();
  const onPageShow = (): void => renderRestoredScroll();
  // Navigation can be cancelled, and beforeunload still paints the old frame.
  // Preserve the live composition when the browser caches this page as well.
  const onPageHide = (event: PageTransitionEvent): void => {if(!event.persisted)runtime.destroy();};
  const runtime: MarketingCompositionRuntime = {
    experience: composition.experience,
    publisher,
    physicalHost,
    get destroyed() { return destroyed; },
    renderCurrent,
    destroy: () => {
      if (destroyed) return;
      destroyed = true;
      lifecycleAbort.abort(new DOMException("Marketing experience destroyed.", "AbortError"));
      targetWindow.removeEventListener("bb:localechange", onLocaleChange);
      targetWindow.removeEventListener("resize", onResize);
      targetWindow.removeEventListener("pageshow", onPageShow);
      targetWindow.removeEventListener("pagehide", onPageHide);
      physicalHost.destroy();
      cinema?.destroy();
      publisher.destroy();
      composition.experience.destroy();
      delete marketingRoot.dataset.marketingRuntime;
      if (targetWindow.__BB_MARKETING_EXPERIENCE__ === composition.experience) {
        delete targetWindow.__BB_MARKETING_EXPERIENCE__;
      }
      if (targetWindow.__BB_MARKETING_RUNTIME__ === runtime) {
        delete targetWindow.__BB_MARKETING_RUNTIME__;
      }
      if (targetWindow.__BB_MARKETING_HOST__ === publisher) {
        delete targetWindow.__BB_MARKETING_HOST__;
      }
      if (targetWindow.__BB_MARKETING_PHYSICAL_HOST__ === physicalHost) {
        delete targetWindow.__BB_MARKETING_PHYSICAL_HOST__;
      }
      if (targetWindow.__BB_MARKETING_WORLD_MOUNT__ === worldMount) {
        delete targetWindow.__BB_MARKETING_WORLD_MOUNT__;
      }
    },
  };

  targetWindow.__BB_MARKETING_RUNTIME__ = runtime;
  marketingRoot.dataset.marketingRuntime = "active";
  targetWindow.__BB_MARKETING_EXPERIENCE__ = composition.experience;
  targetWindow.__BB_MARKETING_HOST__ = publisher;
  targetWindow.__BB_MARKETING_PHYSICAL_HOST__ = physicalHost;
  targetWindow.addEventListener("bb:localechange", onLocaleChange);
  targetWindow.addEventListener("resize", onResize, { passive: true });
  targetWindow.addEventListener("pageshow", onPageShow);
  targetWindow.addEventListener("pagehide", onPageHide);
  try {
    /* Телефон листается сам. Режиссёр кино — единственный владелец ввода:
       он отменяет колесо и касание и держит на экране ровно одну главу. Это и
       означало «сайт листается только до середины». Маршрут при этом с самого
       начала спроектирован от прокрутки документа (LinearMarketingPhysicalHost),
       а часы разделов умеют идти и без режиссёра (NarrativeClock) — поэтому без
       него телефон получает обычный длинный сайт, а не обрубок.
       Ширина читается один раз, при монтировании: десктоп по построению
       попадает в ту же ветку, что и раньше. Явно переданный mountCinema —
       шов для тестов, его уважаем всегда. */
    const directed=options.mountCinema!==undefined||targetWindow.innerWidth>=700;
    if(directed)cinema=(options.mountCinema??mountCinema)(targetWindow,marketingRoot);
    /* Телефон получает свой пилот маршрута вместо режиссёра кино: Акт 0 там
       листается сам, а маршрут идёт разделами по импульсу и по стрелке. */
    else cinema=mountMobileRoute(targetWindow,marketingRoot);
    loadOffers(targetDocument.documentElement.lang);
    renderCurrent();
  } catch (error) {
    console.error("[marketing bootstrap]", error);
    runtime.destroy();
    targetWindow.__BB_RECOVER_MARKETING_BOOTSTRAP__?.();
    targetDocument.documentElement.dataset.webglFallback = "true";
    targetDocument.documentElement.dataset.marketingBootstrapFallback = "true";
    targetDocument.body?.classList.remove("marketing-runtime-pending");
    marketingRoot.dataset.marketingStaticFallback = "true";
    return null;
  }
  targetDocument.body?.classList.remove("marketing-runtime-pending");
  const fontsReady = targetDocument.fonts?.ready;
  if (fontsReady) {
    void fontsReady.then(() => {
      if (!lifecycleAbort.signal.aborted) renderRestoredScroll();
    });
  }
  return runtime;
}

if (
  typeof window !== "undefined"
  && typeof document !== "undefined"
  && document.documentElement.dataset.marketingBootstrapFallback !== "true"
) {
  if(window.BBI18n)void window.BBI18n.ready.then(()=>{localize(document.body);mountMarketingExperience();localize(document.body);});
  else mountMarketingExperience();
}
